import { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import useSWR, { useSWRConfig } from 'swr';
import useDebounce from 'hooks/useDebounce';
import useSWRInfinite from 'swr/infinite';
import useFiltersState from 'state/useFiltersState';
import TrelloColumn from '../TrelloColumn/TrelloColumn';
import { COLUMN_TYPE } from '../TrelloColumn/TrelloColumnTypes';
import {
  BORD_ONE_TONE_ICONS,
  TbordOneToneIconVariant
} from 'components/NudosComponents/BordOneToneIcon/BordOneToneIcon.types';
import { ORDER_SHIPMENT_STATUS, ORDER_STATUS_ID_CANCELLED, SHIPMENT_COLUMN_STATUS_ID } from 'types/orders';
import OperationsCard from 'views/TrackingOrder/components/OperationsCard/OperationsCard';
import OrdersCancelledModal from '../OrdersCancelledModal/OrdersCancelledModal';
import ButtonCancelOrders from '../ButtonCancelOrders/ButtonCancelOrders';
import OrdersTrackingOperationsFilters from '../OrdersTrackingOperationsFilters/OrdersTrackingOperationsFilters';
import useOrdersOperationsModule from './useOrdersOperationsModule';
import UnsuccessfulSearch from 'components/unsuccessfulSearch/UnsuccessfulSearch';
import useCountriesData from './useCountriesData';
import useQueryParams from './useQueryParams';
import { ETA_OVERDUE_VALUE, FILTER_NAMES } from '../OrdersTrackingOperationsFilters/filter.constants';
import { isCountryNameMatching, isStringArray, ORDER_PROPERTY_TO_FILTER_KEYS } from '../../trackingOrder.utils';
import { getKamUserWithOrganizationFilter, getShipmentOrderFinancesCancelled } from 'services/orders';
import useShipmentColumnData from './useShipmentColumnData';
import { useGetSWRInfiniteKey } from 'utils/swrInfinite.utils';
import { processQueryParams } from 'utils/ordersQueryParams.utils';
import { optionsParamsSwr } from 'utils/optionsParamsSwr';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import './OrdersListingOperations.scss';

interface IColumnConfig {
  statusId: SHIPMENT_COLUMN_STATUS_ID;
  title: ORDER_SHIPMENT_STATUS;
  subtitle: string;
  variantIcon: TbordOneToneIconVariant;
  columnType: COLUMN_TYPE;
  hasRefetch?: boolean;
}

const COLUMNS_CONFIG: IColumnConfig[] = [
  {
    statusId: SHIPMENT_COLUMN_STATUS_ID.CONFIRMED,
    title: ORDER_SHIPMENT_STATUS.CONFIRMED,
    subtitle: 'Productos listos para comenzar a gestionar',
    variantIcon: BORD_ONE_TONE_ICONS.check,
    columnType: COLUMN_TYPE.CONFIRMED
  },
  {
    statusId: SHIPMENT_COLUMN_STATUS_ID.PREPARATION,
    title: ORDER_SHIPMENT_STATUS.PREPARATION,
    subtitle: 'Preparando los productos del envío',
    variantIcon: BORD_ONE_TONE_ICONS.box,
    columnType: COLUMN_TYPE.PREPARATION,
    hasRefetch: true
  },
  {
    statusId: SHIPMENT_COLUMN_STATUS_ID.TRANSIT_TO_WAREHOUSE,
    title: ORDER_SHIPMENT_STATUS.TRANSIT_TO_WAREHOUSE,
    subtitle: 'Pasa por bodega antes del destino final',
    variantIcon: BORD_ONE_TONE_ICONS.flag,
    columnType: COLUMN_TYPE.TRANSIT_TO_WAREHOUSE
  },
  {
    statusId: SHIPMENT_COLUMN_STATUS_ID.IN_WAREHOUSE,
    title: ORDER_SHIPMENT_STATUS.IN_WAREHOUSE,
    subtitle: 'Los productos llegaron a bodega',
    variantIcon: BORD_ONE_TONE_ICONS.warehouse,
    columnType: COLUMN_TYPE.IN_WAREHOUSE
  },
  {
    statusId: SHIPMENT_COLUMN_STATUS_ID.TRANSIT_TO_DESTINATION,
    title: ORDER_SHIPMENT_STATUS.TRANSIT_TO_DESTINATION,
    subtitle: 'Se encuentra en proceso de entrega',
    variantIcon: BORD_ONE_TONE_ICONS.truck,
    columnType: COLUMN_TYPE.TRANSIT_TO_DESTINATION
  },
  {
    statusId: SHIPMENT_COLUMN_STATUS_ID.DELIVERED,
    title: ORDER_SHIPMENT_STATUS.DELIVERED,
    subtitle: 'Productos entregados con éxito',
    variantIcon: BORD_ONE_TONE_ICONS.doubleCheck,
    columnType: COLUMN_TYPE.DELIVERED
  }
];

/**
 * Componente que muestra el listado de shipments en formato de operaciones con columnas tipo Trello.
 * Gestiona la visualización de shipments por etapa del proceso logístico.
 * Cada columna realiza su propia petición al API con paginación desde el backend.
 */
const OrdersListingOperations = () => {
  const {
    focusedOrderId,
    setFocusedOrderId,
    focusedFilterValues,
    setFocusedFilterValue,
    sortFilter,
    resetOrdersOperationsState
  } = useOrdersOperationsModule();
  const [openCancelOrdersModal, setOpenCancelOrdersModal] = useState(false);
  const { ordersFilters, setOrdersFilters, ordersSearchbarText, propertyToFilterSearch } = useFiltersState();
  const { countryIdToNameMap } = useCountriesData();
  const { cache } = useSWRConfig();

  const hasResetRef = useRef(false);

  if (!hasResetRef.current) {
    hasResetRef.current = true;
    resetOrdersOperationsState();
    setOrdersFilters([]);
    if (cache instanceof Map) {
      const keysToDelete: (string | symbol)[] = [];
      cache.forEach((value: any, key: any) => {
        if (typeof key === 'string' && key.includes('shipments-by-status')) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach(key => cache.delete(key));
    }
  }

  const { data: kamFiltersData } = useSWR('kam-user-filter', getKamUserWithOrganizationFilter, {
    revalidateOnFocus: false
  });

  const debouncedSearchText = useDebounce(ordersSearchbarText, 500);

  const allQueryParams = useQueryParams({
    focusedFilterValues,
    focusedOrderId,
    ordersFilters,
    sortFilter,
    kamFiltersData,
    searchText: debouncedSearchText,
    propertyToFilterSearch
  });

  const isEtaOverdueFilterActive = useMemo(
    () => focusedFilterValues[FILTER_NAMES.SLA_ETA] === ETA_OVERDUE_VALUE,
    [focusedFilterValues]
  );

  const isShipmentColumnFetchEnabled = useCallback(
    (statusId: SHIPMENT_COLUMN_STATUS_ID): boolean => {
      if (!isEtaOverdueFilterActive) return true;

      return (
        statusId === SHIPMENT_COLUMN_STATUS_ID.TRANSIT_TO_WAREHOUSE ||
        statusId === SHIPMENT_COLUMN_STATUS_ID.TRANSIT_TO_DESTINATION
      );
    },
    [isEtaOverdueFilterActive]
  );

  const focusedCountryNamesForCard = useMemo((): string[] => {
    const val = focusedFilterValues[FILTER_NAMES.COUNTRY_DESTINATION];
    if (val == null) return [];
    if (isStringArray(val)) return val;
    return [];
  }, [focusedFilterValues]);

  const focusedOrganizationNameForCard = useMemo(() => {
    const val = focusedFilterValues[FILTER_NAMES.COMPANY];
    return typeof val === 'string' ? val : null;
  }, [focusedFilterValues]);

  const confirmedColumn = useShipmentColumnData({
    statusId: SHIPMENT_COLUMN_STATUS_ID.CONFIRMED,
    queryParams: allQueryParams,
    enabled: isShipmentColumnFetchEnabled(SHIPMENT_COLUMN_STATUS_ID.CONFIRMED)
  });
  const preparationColumn = useShipmentColumnData({
    statusId: SHIPMENT_COLUMN_STATUS_ID.PREPARATION,
    queryParams: allQueryParams,
    enabled: isShipmentColumnFetchEnabled(SHIPMENT_COLUMN_STATUS_ID.PREPARATION)
  });
  const transitWarehouseColumn = useShipmentColumnData({
    statusId: SHIPMENT_COLUMN_STATUS_ID.TRANSIT_TO_WAREHOUSE,
    queryParams: allQueryParams,
    enabled: isShipmentColumnFetchEnabled(SHIPMENT_COLUMN_STATUS_ID.TRANSIT_TO_WAREHOUSE)
  });
  const inWarehouseColumn = useShipmentColumnData({
    statusId: SHIPMENT_COLUMN_STATUS_ID.IN_WAREHOUSE,
    queryParams: allQueryParams,
    enabled: isShipmentColumnFetchEnabled(SHIPMENT_COLUMN_STATUS_ID.IN_WAREHOUSE)
  });
  const transitDestinationColumn = useShipmentColumnData({
    statusId: SHIPMENT_COLUMN_STATUS_ID.TRANSIT_TO_DESTINATION,
    queryParams: allQueryParams,
    enabled: isShipmentColumnFetchEnabled(SHIPMENT_COLUMN_STATUS_ID.TRANSIT_TO_DESTINATION)
  });
  const deliveredColumn = useShipmentColumnData({
    statusId: SHIPMENT_COLUMN_STATUS_ID.DELIVERED,
    queryParams: allQueryParams,
    enabled: isShipmentColumnFetchEnabled(SHIPMENT_COLUMN_STATUS_ID.DELIVERED)
  });

  const columnsData = useMemo(
    () => [
      confirmedColumn,
      preparationColumn,
      transitWarehouseColumn,
      inWarehouseColumn,
      transitDestinationColumn,
      deliveredColumn
    ],
    [
      confirmedColumn,
      preparationColumn,
      transitWarehouseColumn,
      inWarehouseColumn,
      transitDestinationColumn,
      deliveredColumn
    ]
  );

  const SESSION_STORAGE_KEY_RETURNING = 'tracking-order-ops-returning';

  const refetchColumnsIfReturning = (columnsData: Array<{ mutate: () => Promise<void> }>) => {
    try {
      if (sessionStorage.getItem(SESSION_STORAGE_KEY_RETURNING) === '1') {
        sessionStorage.removeItem(SESSION_STORAGE_KEY_RETURNING);
        void Promise.all(columnsData.map(col => col.mutate()));
      }
    } catch {
      displayErrorNotification();
    }
  };

  const setReturningFlag = () => {
    try {
      sessionStorage.setItem(SESSION_STORAGE_KEY_RETURNING, '1');
    } catch {
      displayErrorNotification();
    }
  };

  useEffect(() => {
    refetchColumnsIfReturning(columnsData);
    return setReturningFlag;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isLoadingAnyColumn = useMemo(() => columnsData.some(column => column.isLoading), [columnsData]);

  const hasNoResults = useMemo(() => {
    const allColumnsFinishedLoading = columnsData.every(column => !column.isLoading);
    const allColumnsEmpty = columnsData.every(column => !column.items || column.items.length === 0);
    return allColumnsFinishedLoading && allColumnsEmpty;
  }, [columnsData]);

  const handleCountryClick = useCallback(
    (countryId: number | null, countryName?: string) => {
      const name = countryName || (countryId ? countryIdToNameMap.get(countryId) : null);
      if (!name) return;

      const current = focusedFilterValues[FILTER_NAMES.COUNTRY_DESTINATION];
      const currentNames: string[] = current != null && isStringArray(current) ? current : [];
      const matchIndex = currentNames.findIndex(selected => isCountryNameMatching(name, selected));
      const isAlreadySelected = matchIndex >= 0;
      const newNames = isAlreadySelected ? currentNames.filter((_, i) => i !== matchIndex) : [...currentNames, name];
      const nextFocused = newNames.length > 0 ? newNames : null;
      setFocusedFilterValue(FILTER_NAMES.COUNTRY_DESTINATION, nextFocused);

      const currentFilters = [...ordersFilters];
      const countryFilterIndex = currentFilters.findIndex(filter => filter.name === FILTER_NAMES.COUNTRY_DESTINATION);

      if (nextFocused !== null) {
        if (countryFilterIndex >= 0) {
          currentFilters[countryFilterIndex] = { ...currentFilters[countryFilterIndex], values: nextFocused };
        } else {
          currentFilters.push({ name: FILTER_NAMES.COUNTRY_DESTINATION, includes: 'in', values: nextFocused });
        }
        setOrdersFilters(currentFilters);
      } else if (countryFilterIndex >= 0) {
        currentFilters.splice(countryFilterIndex, 1);
        setOrdersFilters(currentFilters);
      }
    },
    [focusedFilterValues, setFocusedFilterValue, ordersFilters, setOrdersFilters, countryIdToNameMap]
  );

  const handleOrganizationClick = useCallback(
    (organizationName: string | null) => {
      const currentOrg = focusedFilterValues[FILTER_NAMES.COMPANY];
      const current = typeof currentOrg === 'string' ? currentOrg : null;
      const newFocusedOrganizationName = current === organizationName ? null : organizationName;
      setFocusedFilterValue(FILTER_NAMES.COMPANY, newFocusedOrganizationName);
    },
    [focusedFilterValues, setFocusedFilterValue]
  );

  const handleOpenOrCloseCancelOrdersModal = () => setOpenCancelOrdersModal(!openCancelOrdersModal);

  const handleFocusOrder = useCallback(
    (orderId: number | null) => {
      setFocusedOrderId(orderId);
    },
    [setFocusedOrderId]
  );

  const parseOrderIdFromSearchText = (searchText: string): number | null => {
    const trimmedText = searchText.trim();
    if (!trimmedText) return null;

    const parsedOrderId = parseInt(trimmedText, 10);
    return !isNaN(parsedOrderId) && parsedOrderId > 0 ? parsedOrderId : null;
  };

  const getOnRefetch = useCallback((config: typeof COLUMNS_CONFIG[number], columnData: typeof columnsData[number]) => {
    if (!config.hasRefetch) return undefined;

    return config.columnType === COLUMN_TYPE.PREPARATION ? columnData.revalidateFirstPage : columnData.mutate;
  }, []);

  const columns = useMemo(
    () =>
      COLUMNS_CONFIG.map((config, index) => {
        const columnData = columnsData[index];
        return {
          title: config.title,
          subtitle: config.subtitle,
          variantIcon: config.variantIcon,
          items: columnData.items,
          isLoading: columnData.isLoading,
          isLoadingMoreItems: columnData.isLoadingMore,
          hasMoreItems: columnData.hasMore,
          onLoadMore: columnData.loadMore,
          onRefetch: getOnRefetch(config, columnData),
          columnType: config.columnType
        };
      }),
    [columnsData, getOnRefetch]
  );

  useEffect(() => {
    const isOrderNumberSearch = propertyToFilterSearch?.propertyToFilter === ORDER_PROPERTY_TO_FILTER_KEYS.ORDER_NUMBER;

    const orderId = isOrderNumberSearch ? parseOrderIdFromSearchText(debouncedSearchText) : null;

    setFocusedOrderId(orderId);
  }, [debouncedSearchText, propertyToFilterSearch?.propertyToFilter, setFocusedOrderId]);

  const swrKeyCancelledPrefetch = useMemo(
    () => (allQueryParams ? ['orders-cancelled-modal', ORDER_STATUS_ID_CANCELLED, allQueryParams] : null),
    [allQueryParams]
  );
  const getCancelledOrdersPrefetchKey = useGetSWRInfiniteKey(swrKeyCancelledPrefetch);
  const { data: cancelledOrdersPagesPrefetch = [] } = useSWRInfinite(
    getCancelledOrdersPrefetchKey,
    (key: (string | number)[]) => {
      const pageIndex = key?.[key?.length - 1] as number;
      const processedParams = processQueryParams(allQueryParams);
      return getShipmentOrderFinancesCancelled({
        page: pageIndex + 1,
        size: 10,
        additionalQueryParams: processedParams
      });
    },
    {
      ...optionsParamsSwr({ errorRetryCount: 0, suspense: false }),
      revalidateFirstPage: false,
      revalidateOnFocus: false
    }
  );
  const hasCancelledOrdersMatchingSearch = !!ordersSearchbarText && cancelledOrdersPagesPrefetch.flat().length > 0;
  const showCanceledOrders = hasCancelledOrdersMatchingSearch;

  return (
    <div id="ordersListingOperations">
      <div className="containerHeaderOperations">
        <OrdersTrackingOperationsFilters />
        <ButtonCancelOrders
          handleClick={handleOpenOrCloseCancelOrdersModal}
          buttonText="Ver canceladas"
          showCanceledOrders={showCanceledOrders}
        />
      </div>
      {hasNoResults ? (
        <UnsuccessfulSearch customClassName="noResultsFallback" customTextClassName="noResultsFallbackText" />
      ) : (
        <div className="columnsContainerOperations">
          {columns.map(column => (
            <TrelloColumn
              key={column.title}
              title={column.title}
              subtitle={column.subtitle}
              variantIcon={column.variantIcon}
              items={column?.items}
              isLoading={column?.isLoading || isLoadingAnyColumn}
              columnType={column.columnType}
              content={column?.items?.map(item => (
                <OperationsCard
                  key={item?.orderId}
                  data={item}
                  onCountryClick={handleCountryClick}
                  onOrganizationClick={handleOrganizationClick}
                  onRefetch={column.onRefetch}
                  onFocusOrder={handleFocusOrder}
                  focusedOrderId={focusedOrderId}
                  focusedCountryNames={focusedCountryNamesForCard}
                  focusedOrganizationName={focusedOrganizationNameForCard}
                />
              ))}
              onLoadMore={column?.onLoadMore}
              hasMoreItems={column?.hasMoreItems}
              isLoadingMoreItems={column?.isLoadingMoreItems}
              trelloColumnHeaderCustomClassName="trelloColumnHeaderOperations"
            />
          ))}
        </div>
      )}
      {openCancelOrdersModal && (
        <OrdersCancelledModal isOpen={openCancelOrdersModal} onClose={handleOpenOrCloseCancelOrdersModal} />
      )}
    </div>
  );
};

export default OrdersListingOperations;
